import styled from "styled-components";
import { motion } from "framer-motion";
import { Compass, Map, Brain, FileQuestion, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useScroll, useTransform } from "framer-motion";

/* ================= SECTION ================= */

const Section = styled.section`
  position: relative;
  padding: 160px 20px;
  overflow: hidden;

  background: radial-gradient(
      1000px at 16% -10%,
      rgba(245, 158, 11, 0.12),
      transparent 55%
    ),
    radial-gradient(800px at 82% 30%, rgba(56, 189, 248, 0.1), transparent 60%),
    linear-gradient(
      180deg,
      ${({ theme }) => theme.colors.bg},
      rgba(2, 6, 23, 0.95)
    );
`;

/* ================= DOTTED TIMELINE ================= */

const TimeLine = styled.div`
  position: absolute;
  top: -20%;
  left: 50%;
  height: 140%;
  width: 2px;
  transform: translateX(-50%);
  pointer-events: none;

  background-image: repeating-linear-gradient(
    to bottom,
    rgba(245, 158, 11, 0.35) 0px,
    rgba(245, 158, 11, 0.35) 4px,
    transparent 4px,
    transparent 12px
  );
`;

const TimeDot = styled(motion.div)`
  position: absolute;
  left: 50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f59e0b;
  transform: translateX(-50%);
  box-shadow: 0 0 14px rgba(245, 158, 11, 0.55);
`;

/* ================= HEADER ================= */

const Heading = styled(motion.h2)`
  text-align: center;
  font-size: clamp(32px, 4.5vw, 46px);
  font-weight: 900;
  color: ${({ theme }) => theme.colors.text};
`;

const Sub = styled(motion.p)`
  text-align: center;
  max-width: 720px;
  margin: 20px auto 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 17px;
  line-height: 1.7;
`;

/* ================= GRID ================= */

const Grid = styled(motion.div)`
  max-width: 1050px;
  margin: 80px auto 0;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 36px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

/* ================= CARD ================= */

const Card = styled(motion.div)`
  position: relative;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.colors.cardBg},
    ${({ theme }) => theme.colors.bg}
  );
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: 42px 38px;
  overflow: hidden;

  box-shadow: 0 30px 80px rgba(2, 6, 23, 0.85),
    inset 0 1px 0 rgba(255, 255, 255, 0.03);

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      600px at 20% 0%,
      rgba(245, 158, 11, var(--glow)),
      transparent 60%
    );
    opacity: 0;
    transition: opacity 0.4s ease;
  }

  &:hover:before {
    opacity: 1;
  }
`;

/* ================= CLOCK ================= */

const ClockWrap = styled(motion.div)`
  position: absolute;
  top: 16px;
  right: 16px;
  color: rgba(245, 158, 11, 0.4);
`;

/* ================= ICON ================= */

const IconWrap = styled(motion.div)`
  width: 56px;
  height: 56px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  margin-bottom: 24px;
  color: #fff;

  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  box-shadow: 0 0 40px rgba(56, 189, 248, 0.35);
`;

/* ================= TEXT ================= */

const TitleText = styled.p`
  font-size: 19px;
  font-weight: 900;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.4;
  margin-bottom: 10px;
`;

const SubText = styled.p`
  font-size: 15.5px;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.6;
`;

const CTAGroup = styled.div`
  margin: 90px auto 0;
  display: flex;
  justify-content: center;
  gap: 18px;
  flex-wrap: wrap;
`;
const SecondaryCTA = styled.button`
  padding: 16px 26px;
  border-radius: 999px;
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};

  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(14px) saturate(150%);
  border: 1px solid ${({ theme }) => theme.colors.border};

  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }
`;

const PrimaryCTA = styled.button`
  padding: 16px 30px;
  border-radius: 999px;
  font-size: 16px;
  font-weight: 800;
  color: #04111f;

  background: linear-gradient(135deg, #f59e0b, #facc15);
  box-shadow: 0 10px 40px rgba(245, 158, 11, 0.35);

  border: none;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    transform: translateY(-2px) scale(1.03);
    box-shadow: 0 16px 55px rgba(245, 158, 11, 0.48);
  }
`;
const SmallText = styled.p`
  margin-top: 14px;
  font-size: 13px;
  text-align: center;
  color: ${({ theme }) => theme.colors.textMuted};
  opacity: 0.8;
`;

/* ================= ANIMATIONS ================= */

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.18 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 44 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

/* ================= DATA ================= */

const PROBLEMS = [
  {
    icon: Compass,
    title: "Too many technologies, no personal direction",
    sub: "The internet gives endless options, but very little clarity about what your next move should actually be.",
  },
  {
    icon: Map,
    title: "Roadmaps exist, but execution breaks",
    sub: "People save roadmaps, watch videos, start strong for a few days, and then quietly slide back into random learning.",
  },
  {
    icon: Brain,
    title: "DSA vs development vs AI keeps stealing momentum",
    sub: "Switching paths feels productive, but it usually delays the one thing that matters: steady focused progress.",
  },
  {
    icon: FileQuestion,
    title: "Resume bullets grow, confidence does not",
    sub: "Without real projects and a daily system, learners know they are still not fully ready for interviews or actual work.",
  },
];

/* ================= COMPONENT ================= */

export default function ProblemSection() {
  const navigate = useNavigate();

  const { scrollYProgress } = useScroll({
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [0.6, 1]);
  return (
    <Section>
      {/* Dotted timeline */}
      <TimeLine />

      {/* Moving time dots */}
      {[0, 1, 2].map((i) => (
        <TimeDot
          key={i}
          initial={{ y: `${i * -40}%`, opacity: 0 }}
          animate={{ y: "120%", opacity: [0, 1, 1, 0] }}
          transition={{
            duration: 16,
            delay: i * 4,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      ))}

      <Heading
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        You are working hard.
        <br />
        But without a clear roadmap,
        <br />
        hard work turns into random effort.
      </Heading>

      <Sub
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        Most learners do not fail because they are lazy. They fail because they
        keep operating without direction, sequence, and a repeatable daily
        system.
      </Sub>

      <Grid
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-140px" }}
      >
        {PROBLEMS.map((item, i) => {
          const Icon = item.icon;
          return (
            <Card
              key={i}
              variants={fadeUp}
              whileHover={{ y: -14 }}
              transition={{ type: "spring", stiffness: 160 }}
              style={{ "--glow": i * 0.15 + 0.25 }}
            >
              {/* Rotating clock */}
              <ClockWrap
                animate={{ rotate: 360 }}
                transition={{
                  duration: 14,
                  repeat: Infinity,
                  ease: "linear",
                }}
              >
                <Clock size={18} />
              </ClockWrap>

              <IconWrap
                whileHover={{ rotate: -6, scale: 1.06 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <Icon size={28} />
              </IconWrap>

              <TitleText>{item.title}</TitleText>
              <SubText>{item.sub}</SubText>
            </Card>
          );
        })}
      </Grid>
      <CTAGroup>
        <PrimaryCTA onClick={() => navigate("/career-clarity-home")}>
          Create My Free Roadmap →
        </PrimaryCTA>
      </CTAGroup>
      <SmallText>Free roadmap available • No credit card required</SmallText>
    </Section>
  );
}
